import i18n from '@/i18n';
import type { SupportedLocale } from '@/i18n';

/*
 * The bridge between i18next and the plain (non-React) formatters in
 * `lib/format`, `lib/time-labels` and `pages/shared`. Those are pure helpers
 * called from render paths that have no hook context, so they read the active
 * language from the i18next instance rather than from `useTranslation`.
 *
 * Every formatter takes the locale as an optional argument defaulting to
 * `activeLocale()`, which keeps them deterministic in tests.
 */

const DEFAULT_LOCALE: SupportedLocale = 'en';

/** Number of month names a complete `time.months` array carries. */
const MONTHS_IN_YEAR = 12;

/** Fallback month names, used when a locale bundle is missing or malformed. */
const FALLBACK_MONTHS: readonly string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * The active language as a bare tag (`en`, `de`, `es`, `pl`) - i18next may resolve to a
 * region-qualified one (`pl-PL`) which is not a key of our bundles.
 */
export function activeLocale(): string {
  return (i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE).split('-')[0];
}

/**
 * Regional `Intl` tag per UI language. English maps to `en-GB`, not the bare
 * `en` (which Intl reads as US): this is a European community that writes
 * `1 Jul 2026` and `14:05`, and the prototype's screens were drawn that way.
 */
const INTL_TAGS: Record<string, string> = {
  en: 'en-GB',
};

/**
 * A BCP-47 tag for `Intl` formatters - the language's regional variant where
 * one is pinned above, else the bare tag (a valid Intl locale on its own).
 */
export function intlLocale(locale: string = activeLocale()): string {
  return INTL_TAGS[locale] ?? locale;
}

/**
 * Translation lookup for the plain formatters. Namespaced keys only
 * (`common:time.justNow`), so the caller never depends on the default
 * namespace being set.
 */
export function tr(key: string, options?: Record<string, unknown>): string {
  return i18n.t(key, options ?? {});
}

/**
 * Short month names for the active language. Spelled out in the locale bundles
 * rather than taken from `Intl` so the abbreviation is stable across runtimes
 * (ICU disagrees on "Sep" vs "Sept").
 */
export function monthNames(): readonly string[] {
  const months = i18n.t('common:time.months', { returnObjects: true });

  if (!Array.isArray(months) || months.length !== MONTHS_IN_YEAR) {
    return FALLBACK_MONTHS;
  }

  return months.every((month) => typeof month === 'string')
    ? (months as string[])
    : FALLBACK_MONTHS;
}
